import { Box, Button, Flex, Heading, Text } from '@everlywell/ui-kit';
import { createSharedButtonProps } from 'components/PageSections/utils';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import useMdpModals from 'utils/hooks/useMdpModals';

import BulletPointWithIcon, {
  BulletPointWithIconProps,
} from '../BulletPointWithIcon';
import ProductTag from '../ProductTag';

export type ProductDescriptionProps = {
  // Product tags that show at the top of the product description
  flags?: Queries.ContentfulFlag001[] | null;
  title?: string | null;
  price?: {
    dollarAmount?: string | null;
    centsAmount?: string | null;
  };
  frequency?: string | null;
  topBulletPoints?: BulletPointWithIconProps[] | null;
  bottomBulletPoints?: BulletPointWithIconProps[] | null;
  cta?: Queries.Maybe<Queries.ContentfulCallToAction001>;
};

/**
 * ProductDescription component
 * Displays a product description with title, price, bullet points, and CTA
 * To be used in the HeroWithProduct Component. It is meant to be a subsection of a larger component
 * @param {ProductDescriptionProps} props ProductDescriptionProps
 * @param {Queries.ContentfulFlag001[]} [props.flags] Product tags that show at the top of the product description
 * @param {string} [props.title] Product title
 * @param {string} [props.price] Product price
 * @param {string} [props.frequency] Product price frequency
 * @param {BulletPointWithIconProps[]} [props.topBulletPoints] Bullet points to show before the CTA
 * @param {BulletPointWithIconProps[]} [props.bottomBulletPoints] Bullet points to show after the CTA
 * @param {Queries.ContentfulCallToAction001} [props.cta] Call to action button
 *
 * @returns {JSX.Element} containing product description
 */
const ProductDescription = ({
  flags,
  title,
  price,
  frequency,
  topBulletPoints,
  bottomBulletPoints,
  cta,
}: ProductDescriptionProps) => {
  const { handleMdpCtaClick } = useMdpModals();

  const sharedButtonProps = createSharedButtonProps(cta?.url || '');

  const onCtaClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    cta: Queries.ContentfulCallToAction001 | null | undefined,
  ) => {
    handleMdpCtaClick({
      event,
      ctaUrl: cta?.url || '',
    });
    trackCtaClick(cta);
  };

  const trackCtaClick = (
    cta: Queries.ContentfulCallToAction001 | null | undefined,
  ) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'ProductDescription',
        ctaText: cta?.label || 'No CTA label',
      },
    });
  };

  return (
    <Box data-testid="hero-product-description">
      {flags ? (
        <Flex
          marginBottom={[4, null, 6]}
          gap="1"
          justifyContent={['center', null, 'left']}
        >
          {flags?.map((flag) => (
            <ProductTag
              key={flag?.displayText}
              displayText={flag?.displayText}
              metadata={flag?.metadata}
            />
          ))}
        </Flex>
      ) : null}

      {title ? (
        <Heading
          as="h1"
          size="xl"
          marginBottom={[2, null, 3]}
          textAlign={['center', null, 'left']}
        >
          {title}
        </Heading>
      ) : null}

      {price && price?.dollarAmount && price?.centsAmount ? (
        <Flex
          alignItems="baseline"
          justifyContent={['center', null, 'left']}
          whiteSpace="break-spaces"
        >
          <Text fontSize={['lg', null, '2xl']}>$</Text>
          <Text fontSize={['4xl', null, '5xl']}>{price.dollarAmount}</Text>
          <Text fontSize={['lg', null, '2xl']}>.{price.centsAmount}</Text>
          {frequency ? (
            <>
              <Text fontSize="md" color="tints.uiGrey">
                {` /${frequency}`}
              </Text>
            </>
          ) : null}
        </Flex>
      ) : null}

      {topBulletPoints ? (
        <Flex
          marginTop={[4, null, 4]}
          marginBottom={[4, null, 8]}
          gap="2"
          flexDirection="column"
        >
          {topBulletPoints.map((bullet) => (
            <BulletPointWithIcon key={bullet?.text} {...bullet} />
          ))}
        </Flex>
      ) : null}

      {cta && cta?.label && cta?.url ? (
        <Button
          {...sharedButtonProps}
          onClick={(event) => onCtaClick(event, cta)}
          width="100%"
          className={cta?.metadata?.metadata_id ?? ''}
        >
          {cta?.label}
        </Button>
      ) : null}

      {bottomBulletPoints ? (
        <Flex
          marginTop={[6, null, 4]}
          justifyContent="center"
          gap="2"
          flexWrap="wrap"
        >
          {bottomBulletPoints.map((bullet) => (
            <BulletPointWithIcon key={bullet?.text} {...bullet} />
          ))}
        </Flex>
      ) : null}
    </Box>
  );
};

export default ProductDescription;
